import { Column, CustomReportType, FacetEnum, FacetOption } from '@urc-ui/store/api/report-metadata/types';
import { COV_COLUMN_OVERRIDE_MAP, COV_COLUMN_SUFFIX } from '@urc-ui/utils/global-constants';
import { HOURLY_TIME_UNIT_ID } from '@urc-ui/pages/create-subscription/constants';
import { mergeMtaColumns } from '@urc-ui/utils/mta-column-mapper';
import { REPORT_CATEGORY } from '@urc-ui/utils/types';

export interface GetCRTColumnProps {
  /**
   * timeUnits: List of timeunits as facetOption available for a custom report, can be an empty array if not a custom report
   */
  timeUnits: FacetOption[];
  /**
   * customReportColumns: List of Required Columns (Columns present in customReportMetadata.customReportType.columns),
   *                      can be an empty array if not a custom report
   */
  customReportColumns: Column[];
  /**
   * isCustomReport: If selected reportType is a custom Report.
   */
  isCustomReport: boolean;
  /**
   * timeUnitId: The id of the timeunit whose columns should be added to the response. (Should be available as facetOption in @param(timeUnits))
   */
  timeUnitId: string;
}

export const getCRTColumns = (props: GetCRTColumnProps) => {
  const { timeUnits, customReportColumns, isCustomReport, timeUnitId } = props;
  if (!isCustomReport) {
    return [];
  }
  const timeUnitColumns = (timeUnits as FacetOption[])
    .find(timeUnit => timeUnit.id === timeUnitId)
    ?.requiredColumns.map(reqCol => reqCol.id);
  return timeUnitColumns?.concat(customReportColumns.map(col => col.id)) || [];
};

/**
 * Props to get CRT columns
 * @param customReportType - Custom report data
 * @param timeUnitId -time unit selected
 * @param cov - cov
 */
export interface GetCustomReportColumnsProps {
  reportCategory: string;
  customReportType: CustomReportType;
  timeUnitId: string | null;
  cov: string | null | undefined;
  isMta: boolean | null | undefined;
}

/**
 * Method to get the set of columns for the Custom Create subscription. If Cov is not selected, then the method will skip cov columns.
 *
 * Note:
 * Time unit columns are always updated to the start of the columns list.
 * This method will only consider the required columns and unavailable columns in overrides from the CRT response.
 *
 * TODO: Need to add the cov columns in actual order. SIM: https://sim.amazon.com/issues/RCMRX-6824
 * @param props
 */
export const getCustomReportColumns = (props: GetCustomReportColumnsProps): Column[] => {
  if (!props.timeUnitId || !props.customReportType) {
    return [];
  }

  const baseColumns =
    (props.customReportType.columns && props.customReportType.columns.filter((col): col is Column => !!col && !!col.id)) || [];
  const timeUnitColumns = getFacetOptionColumnsByFacetTypeAndId(props.customReportType, FacetEnum.TIME_UNIT, props.timeUnitId);

  const covColumns = getFacetOptionColumnsByFacetTypeAndId(props.customReportType, FacetEnum.COV, FacetEnum.COV);
  let columns = [...timeUnitColumns, ...getColumnsWrtCov(!!props.cov, covColumns, baseColumns)];

  const mtaColumns = getFacetOptionColumnsByFacetTypeAndId(props.customReportType, FacetEnum.MTA, FacetEnum.MTA);
  columns = [...getColumnsWrtMta(!!props.isMta, props.reportCategory, mtaColumns, columns)];

  const unavailableTimeUnitColumns = getFacetOptionUnavailableColumnsByFacetTypeAndId(
    props.customReportType,
    FacetEnum.TIME_UNIT,
    props.timeUnitId
  );

  if (unavailableTimeUnitColumns && unavailableTimeUnitColumns.length > 0) {
    const unavailableTimeUnitColumnIds = getColumnIds(unavailableTimeUnitColumns);
    return columns.filter(col => !unavailableTimeUnitColumnIds.includes(col.id));
  }

  return columns || [];
};

export const getFacetOptionColumnsByFacetTypeAndId = (customReportType: CustomReportType, facetType: FacetEnum, id: string): Column[] => {
  const facetOptions = customReportType.facets.find(facet => facet.id === facetType)?.options || [];
  return facetOptions.find(facetOption => facetOption.id === id)?.requiredColumns || [];
};

const getFacetOptionUnavailableColumnsByFacetTypeAndId = (
  customReportType: CustomReportType,
  facetType: FacetEnum,
  id: string
): Column[] => {
  const facetOptions = customReportType.facets.find(facet => facet.id === facetType)?.options || [];
  return facetOptions.find(facetOption => facetOption.id === id)?.featureOverride?.unavailableColumns || [];
};

export const getColumnIds = (columns: Column[]): string[] => {
  if (!columns || !columns.length) {
    return [];
  }

  // check that the id field exists so we don't end up with undefined values
  if (columns.filter(col => col?.id).length !== columns.length) {
    return [];
  }

  return columns.map(column => column.id) || [];
};

/**
 * Gets valid MTA colums for the request.
 *
 */
const getColumnsWrtMta = (isMtaEnabled: boolean, reportCategory: string, mtaColumns: Column[], originalColumns: Column[]): Column[] => {
  if (!isMtaEnabled || mtaColumns.length === 0) {
    return originalColumns;
  }

  return mergeMtaColumns({
    reportCategory: reportCategory as REPORT_CATEGORY,
    lastTouchColumns: originalColumns,
    mtaColumns: mtaColumns
  });
};

/**
 * Returns an array of updated column
 * if checked then insert cov columns into original columns based on the suffix 'CoV'
 * if not checked, then return original columns
 * @param isCovEnabled flag indicating whether or not cov checkbox is selected
 * @param {String[]} covColumns  the columns after change to cov
 * @param {String[]} originalColumns the columns that in selection
 * @return {String[]} Returns the output columns
 *
 */
const getColumnsWrtCov = (isCovEnabled: boolean, covColumns: Column[], originalColumns: Column[]): Column[] => {
  if (!isCovEnabled || covColumns.length === 0) {
    return originalColumns;
  }

  const mergedList: Column[] = [];
  const originalColumnMap = new Map(originalColumns.map(col => [col.id, col]));
  const unmatchedCovColumns: Column[] = [];
  const covMap = {};

  covColumns.forEach(covColumn => {
    const covColumnId = covColumn.id;
    if (COV_COLUMN_OVERRIDE_MAP.get(covColumnId)) {
      (covMap as any)[COV_COLUMN_OVERRIDE_MAP.get(covColumnId) as string] = covColumn;
    } else if (covColumnId.endsWith(COV_COLUMN_SUFFIX)) {
      const columnId = covColumnId.substring(0, covColumnId.length - COV_COLUMN_SUFFIX.length);
      if (originalColumnMap.get(columnId)) {
        (covMap as any)[columnId] = covColumn;
      } else {
        unmatchedCovColumns.push(covColumn);
      }
    } else {
      unmatchedCovColumns.push(covColumn);
    }
  });

  originalColumns.forEach(column => {
    const covColumn = (covMap as any)[column.id];
    if (covColumn) {
      mergedList.push(covColumn);
    }
    mergedList.push(column);
  });
  mergedList.push(...unmatchedCovColumns);
  return mergedList;
};

export const isCovEnabled = (customReportType: CustomReportType | null): boolean => {
  if (!customReportType) {
    return false;
  }
  const covFacet = getFacetOptionColumnsByFacetTypeAndId(customReportType, FacetEnum.COV, FacetEnum.COV);
  return !(covFacet.length === 0);
};

export const isMTAEnabled = (customReportType: CustomReportType | null): boolean => {
  if (!customReportType) {
    return false;
  }
  const mtaFacet = getFacetOptionColumnsByFacetTypeAndId(customReportType, FacetEnum.MTA, FacetEnum.MTA);
  return !(mtaFacet.length === 0);
};

export const isNonHourlyTimeUnit = (timeUnitId: string | null | undefined) => {
  return timeUnitId && timeUnitId !== HOURLY_TIME_UNIT_ID;
};

export const getFacetOptionByTimeUnitId = (customReportType: CustomReportType | null, timeUnitId: string | null) => {
  if (!customReportType || !timeUnitId) {
    return undefined;
  }

  const facetOptions = customReportType.facets?.find(facet => facet.id === FacetEnum.TIME_UNIT)?.options || [];
  return facetOptions?.find(timeUnit => timeUnit.id === timeUnitId);
};
